// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import { collection, getDocs, query, where } from "firebase/firestore";
import { firestore } from "../../context/FirebaseConfig";
import { startOfDay, endOfDay } from 'date-fns';

import { getAggregatedSupplierObservations, filterOutAggregationsWithLessThanNObservations } from "../../shared_components/utils/ObservationAggregationUtils";

// Fetch suppliers from Firestore
export async function getSupplierDetails(companyId) {
  const suppliersColl = collection(firestore, "clients", companyId, "client_collections", "suppliers", "supplier_details");
  const snapshot = await getDocs(suppliersColl);
  const suppliersData = [];

  snapshot.forEach((doc) => {
    suppliersData.push({
      id: doc.id,
      ...doc.data()
    });
  });

  return suppliersData;
}

// Fetch deliveries for a supplier from Firestore
export async function getDeliveriesForSupplier(companyId, supplierId, dateRange) {
  // Start with a base query
  let deliveriesRef = collection(firestore, "clients", companyId, "deliveries");
  let queryConstraints = [where("supplier_id", "==", supplierId)];
  
  // Add date range filter if both dates are provided and they are not null
  if (dateRange?.startDate) {
    queryConstraints.push(where("date", ">=", startOfDay(new Date(dateRange.startDate))));
  }
  if (dateRange?.endDate) {
    queryConstraints.push(where("date", "<=", endOfDay(new Date(dateRange.endDate))));
  }
  
  // Build and execute the query
  const deliveriesQuery = query(deliveriesRef, ...queryConstraints);
  const snapshot = await getDocs(deliveriesQuery);
  const deliveries = [];

  snapshot.forEach((doc) => {
    deliveries.push({
      id: doc.id,
      ...doc.data()
    });
  });

  return deliveries;
}

// Extract all unique materials from a supplier's deliveries
export function getSupplierMaterials(deliveries) {
  const materials = new Set();
  
  deliveries.forEach(delivery => {
    if (delivery.base_labels && delivery.base_labels.materials) {
      materials.add(delivery.base_labels.materials);
    }
  });
  
  return Array.from(materials);
}

// Aggregate data for each supplier
export async function getSupplierAggregatedData(companyId, supplierId, dateRange) {
  try {
    const observationAggregationResult = await getAggregatedSupplierObservations(firestore, companyId, supplierId, dateRange.startDate, dateRange.endDate, 24*60);
    const aggregatedObservations = observationAggregationResult.reduced_data;
    filterOutAggregationsWithLessThanNObservations(aggregatedObservations, 20);
    
    // Calculate aggregations from deliveries
    const deliveries = await getDeliveriesForSupplier(companyId, supplierId, dateRange);
    
    // Get all unique materials
    const materials = getSupplierMaterials(deliveries);
    
    return {
      deliveries,
      aggregatedObservations,
      materials,
    };
  } catch (error) {
    console.error(`Error aggregating data for supplier ${supplierId}:`, error);
    return {
      deliveries: [],
      aggregatedObservations: {},
      materials: [],
    };
  }
}

/**
 * Filters supplier data based on the selected filters
 * @param {Object} params - The filter parameters
 * @param {Object} params.suppliersData - The suppliers data
 * @param {string} [params.materialFilter=null] - The selected material filter
 * @param {number} [params.minDeliveriesFilter=null] - The minimum number of deliveries
 * @param {number} [params.maxDeliveriesFilter=null] - The maximum number of deliveries
 * @param {number} [params.minTonnageFilter=null] - The minimum tonnage
 * @param {number} [params.maxTonnageFilter=null] - The maximum tonnage
 * @param {number} [params.minBalesInProductionFilter=null] - The minimum number of bales in production
 * @param {number} [params.maxBalesInProductionFilter=null] - The maximum number of bales in production
 * @param {Array} [params.selectedClaimIssuesFilter=null] - The selected claim issues filter
 * @returns {Object} - The filtered suppliers data
 */
export function filterSupplierData({
  suppliersData,
  materialFilter = null,
  minDeliveriesFilter = null,
  maxDeliveriesFilter = null,
  minTonnageFilter = null,
  maxTonnageFilter = null,
  minBalesInProductionFilter = null,
  maxBalesInProductionFilter = null,
  selectedClaimIssuesFilter = null,
}) {
  let filteredSuppliersData = suppliersData;
  
  // Apply material filter if defined
  if (materialFilter !== null && materialFilter !== 'all') {
    filteredSuppliersData = Object.fromEntries(
      Object.entries(filteredSuppliersData).map(([supplierId, supplierData]) => {
        const filteredSupplierData = {};
      
        // Apply material filter
        if (materialFilter !== 'all') {
          // Filter deliveries
          filteredSupplierData.deliveries = supplierData.deliveries.filter(delivery => delivery?.base_labels?.materials === materialFilter);
      
          // Filter observations
          if (materialFilter in (supplierData.aggregatedObservations?.data_per_material || {})) {
            filteredSupplierData.aggregatedObservations = supplierData.aggregatedObservations.data_per_material[materialFilter];
          } else {
            filteredSupplierData.aggregatedObservations = {
              data_per_labelset: {},
              total_count: 0,
              total_weight_kg: 0
            };
          }
        }
        return [supplierId, filteredSupplierData];
      })
    );
  }

  // Apply deliveries range filter if defined
  if (minDeliveriesFilter !== null || maxDeliveriesFilter !== null) {
    filteredSuppliersData = filterSupplierDataOutsideDeliveriesRange(filteredSuppliersData, minDeliveriesFilter, maxDeliveriesFilter);
  }

  // Apply tonnage range filter if defined
  if (minTonnageFilter !== null || maxTonnageFilter !== null) {
    filteredSuppliersData = filterSupplierDataOutsideTonnageRange(filteredSuppliersData, minTonnageFilter, maxTonnageFilter);
  }

  // Apply bales in production range filter if defined
  if (minBalesInProductionFilter !== null || maxBalesInProductionFilter !== null) {
    filteredSuppliersData = filterSupplierDataOutsideBalesInProductionRange(filteredSuppliersData, minBalesInProductionFilter, maxBalesInProductionFilter);
  }

  // Apply claim issues filter if defined
  if (selectedClaimIssuesFilter !== null && selectedClaimIssuesFilter.length > 0) {
    filteredSuppliersData = filterSelectedDeliveryClaimIssues(filteredSuppliersData, selectedClaimIssuesFilter);
  }

  return filteredSuppliersData;
}

/**
 * Filters suppliers based on the number of deliveries
 * @param {Object} suppliersData - The suppliers data
 * @param {number | null} min - The minimum number of deliveries (null for no minimum)
 * @param {number | null} max - The maximum number of deliveries (null for no maximum)
 * @returns {Object} - The filtered suppliers data
 */
export function filterSupplierDataOutsideDeliveriesRange(suppliersData, min = null, max = null) {
  return Object.fromEntries(
    Object.entries(suppliersData).filter(([_, data]) => {
      const numDeliveries = (data.deliveries || []).length;
      return (min === null || numDeliveries >= min) && (max === null || numDeliveries <= max);
    })
  );
}

/**
 * Filters suppliers based on the total tonnage of their deliveries
 * @param {Object} suppliersData - The suppliers data
 * @param {number | null} minTons - The minimum total tonnage (null for no minimum)
 * @param {number | null} maxTons - The maximum total tonnage (null for no maximum)
 * @returns {Object} - The filtered suppliers data
 */
export function filterSupplierDataOutsideTonnageRange(suppliersData, minTons = null, maxTons = null) {
  if (minTons === null && maxTons === null) return suppliersData; // No filter applied

  return Object.fromEntries(
    Object.entries(suppliersData).filter(([_, data]) => {
      const totalTons = getSupplierTotalWeightTons(data);
      return (minTons === null || totalTons >= minTons) && (maxTons === null || totalTons <= maxTons);
    })
  );
}

export function filterSelectedDeliveryClaimIssues(suppliersData, selectedClaimIssuesFilter) {
  // Run through all suppliers
  return Object.fromEntries(
    Object.entries(suppliersData).map(([supplierId, supplierData]) => {
      const filteredSupplierData = { ...supplierData };
      
      // Run through all deliveries
      filteredSupplierData.deliveries = (supplierData?.deliveries || []).map((delivery) => {

        // Delivery has no claim -> keep as is
        if (!delivery?.claim_data) {
          return delivery;
        }
        
        // Delivery has claim that is inside the filter selection
        if (selectedClaimIssuesFilter.some(claimIssue => (delivery?.claim_data?.delivery_issues || []).includes(claimIssue))) {
          return delivery;
        }

        // Delivery has no delivery issues and the filter includes "claim_without_issues" -> keep as is
        if (selectedClaimIssuesFilter.includes('claim_without_issues') && !delivery?.claim_data?.delivery_issues) {
          return delivery;
        }

        // Delivery has claim that is not inside the filter selection -> remove the claim_data
        return { ...delivery, claim_data: null };
      })

      return [supplierId, filteredSupplierData];
    })
  );
}

export function filterSupplierDataOutsideBalesInProductionRange(suppliersData, minBales = null, maxBales = null) {
  if (minBales === null && maxBales === null) return suppliersData; // No filter applied

  return Object.fromEntries(
    Object.entries(suppliersData).filter(([_, data]) => {
      const totalBales = getSupplierNumberOfBalesInProduction(data);
      return (minBales === null || totalBales >= minBales) && (maxBales === null || totalBales <= maxBales);
    })
  );
}

/**
 * Filters suppliers based on search query
 * @param {Array} suppliers - Array of supplier objects
 * @param {string} searchQuery - Text to search for in name or code
 * @returns {Array} - Filtered supplier array
 */
export function filterSuppliersBySearchQuery(suppliers, searchQuery) {
  if (!suppliers.length) return [];
  
  return suppliers.filter(supplier => {
    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase().trim();
      const name = (supplier.display_name || supplier.name || '').toLowerCase();
      const code = (supplier.supplier_code || '').toLowerCase();
      
      if (!name.includes(query) && !code.includes(query)) {
        return false;
      }
    }
    
    return true;
  });
}

/**
 * Get the number of deliveries for a single supplier
 * @param {Object} supplierData - The supplier's detailed data
 * @returns {number} - The number of deliveries
 */
export function getSupplierNumberOfDeliveries(supplierData) {
  return (supplierData?.deliveries || []).length;
}

/**
 * Get the total weight of a single supplier's deliveries in tons
 * @param {Object} supplierData - The supplier's detailed data
 * @returns {number} - The total weight in tons
 */
export function getSupplierTotalWeightTons(supplierData) {
  return (supplierData?.deliveries || []).reduce((acc, delivery) => {
    const weightKg = parseFloat(
      delivery?.documents_data?.complete_data?.weight_kg_netto_2 || 
      delivery?.documents_data?.complete_data?.weight_kg_netto_1
    );
    if (!isNaN(weightKg)) {
      return acc + weightKg / 1000; // Convert kg to tons
    } else {
      return acc;
    }
  }, 0);
}

/**
 * Get the number of claims for a single supplier
 * @param {Object} supplierData - The supplier's detailed data
 * @returns {number} - The number of claims
 */
export function getSupplierNumberOfClaims(supplierData) {
  return (supplierData?.deliveries || []).reduce((acc, delivery) => {
    const hasClaim = delivery?.claim_data?.claim_nr;
    return acc + (hasClaim ? 1 : 0);
  }, 0);
}

/**
 * Get the number of bales in production for a single supplier
 * @param {Object} supplierData - The supplier's detailed data
 * @returns {number} - The number of bales in production
 */
export function getSupplierNumberOfBalesInProduction(supplierData) {
  // For now, assuming each delivery represents one bale in production
  // This can be modified based on actual business logic for calculating bales
  return supplierData?.aggregatedObservations?.total_count || 0;
}